<template>
  <div class="wallet-details-container">
    <!-- Breadcrumbs and Header -->
    <div class="header-section">
      <div class="breadcrumbs">
        <a-breadcrumb>
          <a-breadcrumb-item>
            <a @click="$router.push('/financing-and-payments')"
              >Financing & Payments</a
            >
          </a-breadcrumb-item>
          <a-breadcrumb-item>
            <a @click="$router.push('/financing-and-payments?tab=wallets')"
              >Wallets Dashboard</a
            >
          </a-breadcrumb-item>
          <a-breadcrumb-item
            >{{ walletData.ownerName || "Loading" }} - Wallet</a-breadcrumb-item
          >
        </a-breadcrumb>
      </div>

      <div class="header-content">
        <div class="title-section">
          <h1>{{ walletData.ownerName || "Loading" }} - Wallet</h1>
          <p class="wallet-id"># {{ walletData.code || "Loading..." }}</p>
        </div>
        <a-button
          type="primary"
          @click="$router.push('/financing-and-payments?tab=wallets')"
          style="background: #26a246; border-color: #26a246"
        >
          Back to Dashboard
        </a-button>
      </div>
    </div>

    <div class="main-content">
      <!-- Left Sidebar -->
      <div class="sidebar">
        <!-- User Profile -->
        <div class="user-profile">
          <div class="avatar">
            {{ getInitials(walletData.ownerName) }}
          </div>
          <h3>{{ walletData.ownerName || "Loading..." }}</h3>
          <p class="user-id">
            Wallet Code: {{ walletData.code || "Loading..." }}
          </p>
        </div>

        <!-- Wallet Details -->
        <div class="wallet-details">
          <h3>Account Details</h3>
          <div class="detail-item">
            <span class="label">Type:</span>
            <span class="value">{{
              walletData.walletType || "Loading..."
            }}</span>
          </div>
          <div class="detail-item">
            <span class="label">KYC Status:</span>
            <span class="value">
              <StatusTag :status="walletData.kycStatus || 'Loading...'" />
            </span>
          </div>
          <div class="detail-item">
            <span class="label">Account Status:</span>
            <span class="value">
              <StatusTag :status="walletData.status || 'Loading...'" />
            </span>
          </div>
          <div class="detail-item">
            <span class="label">Date Created:</span>
            <span class="value">{{
              formatDate(walletData.createdAt) || "Loading..."
            }}</span>
          </div>
        </div>

        <!-- Wallet Balances -->
        <div class="wallet-balances">
          <h3>Wallet Balance</h3>
          <div class="wallet-balance-item">
            <div class="wallet-balance">
              <span class="balance-amount"
                >{{
                  walletData.walletCurrency ? walletData.walletCurrency : "-"
                }}
                {{ walletData.walletBalance }}</span
              >
              <span class="wallet-code">{{ walletData.walletCode }}</span>
            </div>
          </div>
        </div>

        <!-- Overdraft Facility -->
        <div
          v-if="walletData.hasOverdraft"
          class="wallet-balances overdraft-section"
        >
          <h3>Overdraft Facility</h3>
          <div class="wallet-balance-item overdraft-item">
            <div class="wallet-balance">
              <span class="balance-amount">
                {{ walletData.overdraftCurrency }}
                {{ formatAmount(walletData.overdraftBalance) }}
              </span>
              <span class="wallet-code">Overdraft Balance</span>
            </div>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="quick-actions">
          <h3>Quick Actions</h3>
          <a-button
            v-if="walletData.status !== 'Inactive'"
            type="danger"
            icon="stop"
            @click="suspendWallet"
            style="width: 100%; margin-bottom: 8px"
            :loading="actionLoading"
          >
            Suspend Wallet
          </a-button>
          <a-button
            v-if="walletData.status === 'Inactive'"
            type="primary"
            icon="check"
            @click="activateWallet"
            style="
              width: 100%;
              margin-bottom: 8px;
              background: #26a246;
              border-color: #26a246;
            "
            :loading="actionLoading"
          >
            Activate Wallet
          </a-button>
        </div>
      </div>

      <!-- Right Panel -->
      <div class="content-panel">
        <!-- Tabs -->
        <a-tabs v-model="activeTab" class="green-tabs" :animated="false">
          <a-tab-pane key="transactions" tab="Transaction History">
            <!-- Filters -->
                         <div class="filters-section">
               <div class="filter-group">
                 <span class="filter-label">Filter by:</span>
                 <a-range-picker
                   v-model="filters.dateRange"
                   placeholder="['Start Date', 'End Date']"
                   style="width: 250px; margin-right: 16px"
                   format="YYYY-MM-DD"
                   :allowClear="true"
                 />
                 
               </div>
             </div>

            <!-- Transactions Table -->
            <a-table
              :columns="transactionColumns"
              :data-source="transactions"
              :pagination="pagination"
              :loading="transactionLoading"
              row-key="id"
              :scroll="{ y: 400 }"
              @change="handleTableChange"
            >
              <template slot="amount" slot-scope="text, record">
                <span
                  :style="{
                    color: record.isCredit === 1 ? '#10b981' : '#ef4444',
                  }"
                >
                  {{ record.isCredit === 1 ? "+ " : "- "
                  }}{{ formatAmount(text) }}
                </span>
              </template>
            </a-table>
          </a-tab-pane>

          <a-tab-pane key="linked-accounts" tab="Linked Accounts">
            <!-- Filter Section -->
            <div class="filters-section">
              <div class="filter-group">
                <span class="filter-label">Filter by:</span>
                <a-input-search
                  v-model="filters.accountSearch"
                  placeholder="Search by Account Number"
                  style="width: 250px"
                />
              </div>
            </div>

            <!-- Linked Accounts Table -->
            <a-table
              :columns="linkedAccountColumns"
              :data-source="linkedAccounts"
              :pagination="linkedAccountsPagination"
              :loading="loading"
              row-key="id"
              :scroll="{ y: 400 }"
              class="linked-accounts-table"
            >
              <template slot="status" slot-scope="text">
                <StatusTag :status="text" />
              </template>
            </a-table>
          </a-tab-pane>
        </a-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import StatusTag from "../shared-components/StatusTag.vue";
import { getInitials, formatAmount, formatDate } from "@/utils";
import sellersService from "../../api/sellers.service";

export default {
  name: "WalletDetails",
  components: {
    StatusTag,
  },
  data() {
    return {
      activeTab: "transactions",
      loading: false,
      actionLoading: false,
             filters: {
         dateRange: null, // Date range picker value
         accountSearch: "",
       },
      pagination: {
        pageIndex: 1,
        pageSize: 10,
        total: 0,
        showTotal: (total, range) =>
          `Showing ${range[0]}-${range[1]} of ${total} transactions`,
        showSizeChanger: false,
      },
      linkedAccountsPagination: {
        pageIndex: 1,
        pageSize: 10,
        total: 10,
        showTotal: (total, range) =>
          `Showing ${range[0]} to ${range[1]} of ${total} linked accounts`,
      },
      walletData: {
        id: "",
        firstName: "",
        middleName: "",
        lastName: "",
        ownerName: "",
        code: "",
        phone: "",
        email: "",
        nationalId: "",
        status: "",
        kycStatus: "",
        creditScore: {},
        company: {},
        createdAt: "",
        wallets: [],
        groups: [],
      },
      transactions: [],
      transactionLoading: false,
      transactionColumns: [
        {
          title: "Date",
          dataIndex: "createdAt",
          key: "createdAt",
          customRender: (text) => moment(text).format("MMM DD, YYYY"),
        },
        {
          title: "Type",
          dataIndex: "isCredit",
          key: "type",
          customRender: (text) => (text === 1 ? "Credit" : "Debit"),
        },
        {
          title: "Method",
          dataIndex: "methodType",
          key: "methodType",
        },
        {
          title: "Amount",
          dataIndex: "amount",
          key: "amount",
          scopedSlots: { customRender: "amount" },
        },
      ],
      linkedAccounts: [],
      linkedAccountColumns: [
        {
          title: "Bank Name",
          dataIndex: "bankName",
          key: "bankName",
        },
        {
          title: "Account Name",
          dataIndex: "accountName",
          key: "accountName",
        },
        {
          title: "Account Number",
          dataIndex: "accountNumber",
          key: "accountNumber",
        },
        {
          title: "Account Type",
          dataIndex: "accountTypeDetail",
          key: "accountTypeDetail",
        },
      ],
    };
  },

  methods: {
    getInitials,
    formatAmount,
    formatDate,
    async loadWalletData(walletId) {
      this.loading = true;
      try {
        const response = await sellersService.getSingleWallet(walletId);

        if (response.status === 200 && response.data?.data) {
          const data = response.data.data;

          // Create full name
          const fullName =
            [data.firstName || "", data.middleName || "", data.lastName || ""]
              .filter(Boolean)
              .join(" ") || "N/A";

          // Find the specific Seller or Buyer wallet
          let walletDetails = null;
          if (data.wallets && data.wallets.length > 0) {
            walletDetails = data.wallets.find(
              (wallet) =>
                (wallet.code === walletId ||
                  wallet.id === walletId ||
                  wallet.id.toString() === walletId) &&
                (wallet.name === "Seller" || wallet.name === "Buyer")
            );
          }

          // If no specific wallet found, use the first Seller or Buyer wallet
          if (!walletDetails && data.wallets && data.wallets.length > 0) {
            walletDetails = data.wallets.find(
              (wallet) => wallet.name === "Seller" || wallet.name === "Buyer"
            );
          }

          // Find overdraft wallet
          const overdraftWallet = data.wallets.find(
            (wallet) => wallet.name === "Over Draft"
          );

          this.walletData = {
            ...data,
            ownerName: fullName,
            // Direct wallet details on walletData
            walletId: walletDetails?.id,
            walletCode: walletDetails?.code,
            walletName: walletDetails?.name || "N/A",
            walletBalance: walletDetails?.balance || "0.00",
            walletCurrency: walletDetails?.currency || "KES",
            walletType: walletDetails?.name || "N/A", // Type is the wallet name (Seller/Buyer)
            walletDefault: walletDetails?.default || 0,
            walletStatus: walletDetails?.status || "Active",
            // Ensure other fields have proper fallbacks
            id: data.id || "N/A",
            code: data.code || "N/A",
            phone: data.phone || "N/A",
            email: data.email || "N/A",
            nationalId: data.nationalId || "N/A",
            status: data.status || "Active",
            kycStatus: data.kycStatus || "Pending",
            createdAt: data.createdAt || data.created_at || "N/A",
            creditScore: data.creditScore || {},
            company: data.company || {},
            groups: data.groups || [],
            banks: data.banks || [],
            // Overdraft wallet details
            overdraftBalance: overdraftWallet?.balance || 0,
            overdraftCurrency: overdraftWallet?.currency || "KES",
            hasOverdraft: !!overdraftWallet,
          };

          console.log("Wallet data loaded:", this.walletData);

          // Populate linked accounts from banks data
          this.linkedAccounts = (data.banks || []).map((bank) => ({
            id: bank.id,
            accountType: "Bank",
            accountName: bank.accountName || "N.A",
            accountNumber: bank.accountNumber || "N/A",
            status: "Verified", // Assuming all banks are verified
            bankName: bank.bankName,
            accountTypeDetail: bank.accountType,
            branchName: bank.branchName,
          }));

          // Fetch transactions after wallet data is loaded
          await this.fetchWalletTransactions();
        } else {
          this.$message.error("Failed to load wallet data");
          console.error("API Error:", response);
        }
      } catch (error) {
        console.error("Error loading wallet data:", error);
        this.$message.error("Error loading wallet data");
      } finally {
        this.loading = false;
      }
    },

         async fetchWalletTransactions() {
       if (!this.walletData.walletId) return;

       this.transactionLoading = true;
       try {
         // Convert dateRange to startDate and endDate
         let startDate = "";
         let endDate = "";

         if (this.filters.dateRange && this.filters.dateRange.length === 2) {
           // Create Date objects with time 00:00:00 for start date
           const startDateObj = new Date(this.filters.dateRange[0].format('YYYY-MM-DD'));
           startDateObj.setHours(0, 0, 0, 0);
           startDate = startDateObj;

           // Create Date objects with time 23:59:59 for end date
           const endDateObj = new Date(this.filters.dateRange[1].format('YYYY-MM-DD'));
           endDateObj.setHours(23, 59, 59, 999);
           endDate = endDateObj;
         }

         const params = {
           startDate: startDate,
           endDate: endDate,
           pageIndex: this.pagination.pageIndex,
           pageSize: this.pagination.pageSize,
         };

         const response = await sellersService.getWalletTransactions(
           this.walletData.walletId,
           params
         );

        if (response.status === 200 && response.data?.data) {
          const data = response.data.data;
          console.log("Response data:", data.results);
          this.transactions = data.results
          console.log("Transactions data:", this.transactions);
          this.pagination.total = data.totalCount;
        } else {
          this.transactions = [];
          this.pagination.total = 0;
          this.$message.warning("No transactions found");
        }
      } catch (error) {
        console.error("Error fetching wallet transactions:", error);
        this.$message.error("Error loading transactions");
        this.transactions = [];
        this.pagination.total = 0;
      } finally {
        this.transactionLoading = false;
      }
    },

    async suspendWallet() {
      this.$confirm({
        title: "Suspend Wallet",
        content: `Are you sure you want to suspend ${this.walletData.ownerName}'s wallet?`,
        onOk: async () => {
          await this.updateWalletStatus("Inactive");
        },
      });
    },

    async activateWallet() {
      this.$confirm({
        title: "Activate Wallet",
        content: `Are you sure you want to activate ${this.walletData.ownerName}'s wallet?`,
        onOk: async () => {
          await this.updateWalletStatus("Active");
        },
      });
    },

    async updateWalletStatus(status) {
      this.actionLoading = true;
      try {
        const response = await sellersService.updateWalletStatus(
          this.$route.params.walletId,
          status
        );

        if (response.status === 200) {
          // Update local wallet data
          this.walletData.status = status;

          const action = status === "Inactive" ? "suspended" : "activated";
          this.$message.success(`Wallet ${action} successfully`);

          console.log(`Wallet status updated to: ${status}`);
        } else {
          this.$message.error(
            `Failed to ${status === "Inactive" ? "suspend" : "activate"} wallet`
          );
          console.error("API Error:", response);
        }
      } catch (error) {
        console.error("Error updating wallet status:", error);
        this.$message.error(
          `Error ${status === "Inactive" ? "suspending" : "activating"} wallet`
        );
               } finally {
           this.actionLoading = false;
         }
       },



       handleTableChange(pagination) {
         if (pagination.pageIndex !== this.pagination.pageIndex) {
           this.pagination.pageIndex = pagination.pageIndex;
           this.fetchWalletTransactions();
         }
       },
     },
     watch: {
     "filters.dateRange"() {
       this.pagination.pageIndex = 1; // Reset to first page when filters change
       this.fetchWalletTransactions();
     },
   },
  created() {
    // Get wallet ID from route params
    const walletId = this.$route.params.walletId;
    if (walletId) {
      // In a real app, you would fetch wallet data here
      console.log("Loading wallet:", walletId);
      this.loadWalletData(walletId);
    }
  },

  mounted() {
    // Ensure page starts at top
    this.$nextTick(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
  },
};
</script>

<style scoped>
.wallet-details-container {
  padding: 24px;
  background: #f5f5f5;
}

.header-section {
  margin-bottom: 24px;
}

.breadcrumbs {
  margin-bottom: 16px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-section h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
}

.wallet-id {
  margin: 4px 0 0 0;
  color: #6b7280;
  font-size: 14px;
}

.main-content {
  display: flex;
  gap: 24px;
  min-width: 0;
  overflow: hidden;
}

.sidebar {
  width: 320px;
  flex-shrink: 0;
}

.content-panel {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.user-profile {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 16px;
  text-align: center;
  border: 1px solid #e5e7eb;
}

.avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #26a246;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 600;
  margin: 0 auto 16px;
}

.user-profile h3 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
}

.user-id {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}

.wallet-details {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 16px;
  border: 1px solid #e5e7eb;
}

.wallet-details h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.label {
  color: #6b7280;
  font-size: 14px;
}

.value {
  font-weight: 500;
  color: #1f2937;
}

.value.balance {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.quick-actions {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid #e5e7eb;
}

.quick-actions h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
}

.wallet-balances {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.wallet-balances h3 {
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 8px;
}

.wallet-balance-item {
  padding: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  transition: all 0.2s ease-in-out;
  position: relative;
  overflow: hidden;
}

.wallet-balance-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #26a246;
}

.wallet-balance-item:last-child {
  margin-bottom: 0;
}

.wallet-balance-item::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #26a246, #10b981, #059669);
}

.wallet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.wallet-name {
  font-weight: 700;
  color: #1f2937;
  font-size: 16px;
  text-transform: capitalize;
  letter-spacing: 0.025em;
}

.default-badge {
  background: linear-gradient(135deg, #26a246, #10b981);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  box-shadow: 0 2px 4px rgba(38, 162, 70, 0.3);
}

.wallet-balance {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 10px;
  border: 1px solid rgba(38, 162, 70, 0.15);
}

.balance-amount {
  font-weight: 800;
  color: #1f2937;
  font-size: 20px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  text-align: center;
  letter-spacing: 0.025em;
}

.wallet-code {
  color: #6b7280;
  font-size: 12px;
  font-family: "SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas,
    "Courier New", monospace;
  background: rgba(107, 114, 128, 0.08);
  padding: 6px 10px;
  border-radius: 8px;
  font-weight: 500;
  text-align: center;
  border: 1px solid rgba(107, 114, 128, 0.1);
}

.filters-section {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.filter-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-label {
  color: #6b7280;
  font-size: 14px;
  margin-right: 8px;
}

.empty-state {
  padding: 48px;
  text-align: center;
}

/* Green tabs styling */
.green-tabs >>> .ant-tabs-tab-active {
  color: #26a246 !important;
}

.green-tabs >>> .ant-tabs-ink-bar {
  background-color: #26a246 !important;
}

/* Linked Accounts Table */
.linked-accounts-table {
  width: 100%;
}

.linked-accounts-table >>> .ant-table {
  width: 100%;
}

.linked-accounts-table >>> .ant-table-tbody > tr > td {
  padding: 16px 12px;
}

.linked-accounts-table >>> .ant-table-thead > tr > th {
  padding: 16px 12px;
}

.linked-accounts-table >>> .ant-pagination {
  text-align: center;
  justify-content: center;
  margin-top: 24px;
}

/* Overdraft styling */
.overdraft-section {
  margin-bottom: 16px;
}

.overdraft-item {
  border-color: #f59e0b;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
}

.overdraft-item::before {
  background: linear-gradient(90deg, #f59e0b, #d97706, #b45309);
}

.overdraft-status {
  margin-top: 8px;
  text-align: center;
}

.available-amount {
  font-size: 12px;
  color: #92400e;
  font-weight: 600;
}

/* Responsive design */
@media (max-width: 1024px) {
  .main-content {
    flex-direction: column;
  }

  .sidebar {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .filter-group {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group > * {
    width: 100%;
  }
}
</style>
